import type { FormEvent } from 'react'
import type { DateRange } from 'react-day-picker'

import { format, isValid, parse } from 'date-fns'
import { Info } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  DelegationAssignment,
  DelegationAssignmentDto,
  DelegationRequestDto,
  MyDelegationsParams,
} from '@/utils/api/leave-service/models'

import { UserSearchInput } from '@/components/common/UserSearchInput'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isOrganisationAdminUser } from '@/lib/role-guards'
import {
  useAllDelegations,
  useCreateDelegation,
  useDeactivate,
  useMyDelegations,
} from '@/utils/api/leave-service'

const DEFAULT_FORM_STATE = {
  principalUserId: undefined as number | undefined,
  delegateUserId: undefined as number | undefined,
  backupUserId: undefined as number | undefined,
  validFrom: '',
  validTo: '',
}

function FieldInfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border/80 bg-muted text-xs text-muted-foreground">
          <Info className="h-3 w-3" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs leading-snug text-muted-foreground">
          {text}
        </p>
      </TooltipContent>
    </Tooltip>
  )
}

export function DelegationsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)

  const { user } = useAuth()
  const isOrganisationAdmin = isOrganisationAdminUser(user)

  const listParams = useMemo<MyDelegationsParams>(() => ({
    pageable: { page: 0, size: 25 },
  }), [])

  const listQuery = useMyDelegations(listParams, {
    query: { enabled: !isOrganisationAdmin },
  })
  const allDelegationsQuery = useAllDelegations(listParams, {
    query: { enabled: isOrganisationAdmin },
  })
  const delegationsQuery = isOrganisationAdmin ? allDelegationsQuery : listQuery

  const createMutation = useCreateDelegation({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogOpen(false)
        void delegationsQuery.refetch()
      },
    },
  })
  const deactivateMutation = useDeactivate({
    mutation: {
      onSuccess: () => {
        void delegationsQuery.refetch()
      },
    },
  })

  const delegations = delegationsQuery.data?.data?.content ?? []
  const canSubmit = formState.principalUserId !== undefined
    && formState.delegateUserId !== undefined
    && formState.validFrom.trim() !== ''
    && formState.validTo.trim() !== ''

  const parsedFromDate = formState.validFrom
    ? parse(formState.validFrom, 'yyyy-MM-dd', new Date())
    : undefined
  const parsedToDate = formState.validTo
    ? parse(formState.validTo, 'yyyy-MM-dd', new Date())
    : undefined

  const selectedRange = useMemo<DateRange | undefined>(() => {
    if (!parsedFromDate || !isValid(parsedFromDate)) {
      return undefined
    }
    return {
      from: parsedFromDate,
      to: parsedToDate && isValid(parsedToDate) ? parsedToDate : undefined,
    }
  }, [parsedFromDate, parsedToDate])

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setFormState(prev => ({
      ...prev,
      validFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
      validTo: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
    }))
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || createMutation.isPending) {
      return
    }

    const payload: DelegationRequestDto = {
      principalUserId: formState.principalUserId,
      delegateUserId: formState.delegateUserId!,
      backupUserId: formState.backupUserId,
      validFrom: formState.validFrom.trim(),
      validTo: formState.validTo.trim(),
    }

    createMutation.mutate({ data: payload })
  }

  const openCreateDialog = useCallback(() => {
    setFormState(DEFAULT_FORM_STATE)
    setDialogOpen(true)
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setFormState(DEFAULT_FORM_STATE)
    }
  }, [])

  const handleDeactivate = useCallback((item: DelegationAssignment) => {
    if (!item.id || !item.active || deactivateMutation.isPending) {
      return
    }
    deactivateMutation.mutate({ id: item.id })
  }, [deactivateMutation])

  const columns = useMemo<ColumnConfig<DelegationAssignmentDto>[]>(() => ([
    {
      key: 'principalUser',
      header: 'Principal User',
      cell: (_value, row) => (row.principalUser ? String(row.principalUser.name) : '-'),
    },
    {
      key: 'delegateUser',
      header: 'Delegate User',
      cell: (_value, row) => (row.delegateUser ? String(row.delegateUser.name) : '-'),
    },
    {
      key: 'backupUser',
      header: 'Backup User',
      cell: (_value, row) => (row.backupUser ? String(row.backupUser.name) : '-'),
    },
    {
      key: 'validFrom',
      header: 'Valid From',
      type: 'date',
      cell: value => (value ? format(String(value), 'MMM do, yyyy') : '-'),
    },
    {
      key: 'validTo',
      header: 'Valid To',
      type: 'date',
      cell: value => (value ? format(String(value), 'MMM do, yyyy') : 'Indefinite'),
    },
    {
      key: 'scope',
      header: 'Scope',
      cell: value => (value ? String(value) : '-'),
    },
    {
      key: 'active',
      header: 'Active',
      type: 'boolean',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!row.id || !row.active || deactivateMutation.isPending}
            onClick={() => handleDeactivate(row)}
          >
            Deactivate
          </Button>
        </div>
      ),
    },
  ]), [deactivateMutation.isPending, handleDeactivate])

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Create Delegation</DialogTitle>
            <DialogDescription>Assign a delegate to handle approvals.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                <Label htmlFor="principal-user-id" className="mb-0">
                  Principal User
                </Label>
                <FieldInfoTip text="This is the person who gives the helper power to approve leave." />
              </div>
              <UserSearchInput
                id="principal-user-id"
                label=""
                placeholder="Search principal by email..."
                required
                value={formState.principalUserId}
                onValueChange={userId => setFormState(prev => ({ ...prev, principalUserId: userId }))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                <Label htmlFor="delegate-user-id" className="mb-0">
                  Delegate User
                </Label>
                <FieldInfoTip text="This helper will approve leave while the principal is away." />
              </div>
              <UserSearchInput
                id="delegate-user-id"
                label=""
                placeholder="Search delegate by email..."
                required
                value={formState.delegateUserId}
                onValueChange={userId => setFormState(prev => ({ ...prev, delegateUserId: userId }))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                <Label htmlFor="backup-user-id" className="mb-0">
                  Backup User (Optional)
                </Label>
                <FieldInfoTip text="This helper fills in if the delegate is not available." />
              </div>
              <UserSearchInput
                id="backup-user-id"
                label=""
                placeholder="Search backup user by email..."
                value={formState.backupUserId}
                onValueChange={userId => setFormState(prev => ({ ...prev, backupUserId: userId }))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                <Label className="mb-0">
                  Leave Date Range
                </Label>
                <FieldInfoTip text="Pick the first and last day the helper will cover approvals." />
              </div>
              <DateRangePicker
                selected={selectedRange}
                onSelect={handleDateRangeChange}
                numberOfMonths={2}
                className=""
                disabled={createMutation.isPending}
              />
            </div>
            {createMutation.isError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to create delegation</AlertTitle>
                  <AlertDescription>{createMutation.error?.message ?? 'Please try again.'}</AlertDescription>
                </Alert>
              )
              : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || createMutation.isPending}>
                {createMutation.isPending ? 'Saving...' : 'Create Delegation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Delegations Management</CardTitle>
            <CardDescription>Manage delegation assignments for approvals.</CardDescription>
          </div>
          <Button type="button" onClick={openCreateDialog}>
            Create Delegation
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {listQuery.isPending
            ? (
              <p className="text-sm text-muted-foreground">Loading delegations...</p>
            )
            : null}
          {listQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load delegations</AlertTitle>
                <AlertDescription>{listQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {deactivateMutation.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to deactivate delegation</AlertTitle>
                <AlertDescription>{deactivateMutation.error?.message ?? 'Please try again.'}</AlertDescription>
              </Alert>
            )
            : null}
          <EnhancedDataTable
            data={delegations}
            columns={columns}
            loading={listQuery.isPending}
            emptyStateMessage="No delegations found."
            enableColumnFilters={false}
            enableExport={false}
          />
        </CardContent>
      </Card>
    </div>
  )
}
